import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNewDashboardGraph } from "../../Redux/Actions/dashboardActions";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip
);

const TIME_TABS = ["Day", "Months", "Year"];
const TYPE_MAP = { Day: "week", Months: "month", Year: "year" };
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const getCurrentMonthYear = () => {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
};

const getWeeksInMonth = (month, year) => {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  const startDay = monthStart.getDay();
  const mondayOffset = startDay === 0 ? -6 : 1 - startDay;
  const firstMonday = new Date(monthStart);
  firstMonday.setDate(monthStart.getDate() + mondayOffset);
  let count = 0;
  let cursor = new Date(firstMonday);
  while (cursor <= monthEnd) {
    count++;
    cursor.setDate(cursor.getDate() + 7);
  }
  return count;
};

const LineGraph = () => {
  const dispatch = useDispatch();
  const { month: curMonth, year: curYear } = getCurrentMonthYear();

  const [activeTime, setActiveTime] = useState("Year");
  const [selectedMonth, setSelectedMonth] = useState(curMonth);
  const [selectedYear, setSelectedYear] = useState(curYear);
  const [selectedWeek, setSelectedWeek] = useState(1);

  const {
    newDashboardGraph: dashboardGraph,
    loading: isLoading,
  } = useSelector((state) => state.dashboard || {});

  const totalWeeks = useMemo(
    () => getWeeksInMonth(selectedMonth, selectedYear),
    [selectedMonth, selectedYear]
  );

  useEffect(() => {
    if (selectedWeek > totalWeeks) setSelectedWeek(1);
  }, [totalWeeks, selectedWeek]);

  useEffect(() => {
    if (activeTime === "Day") setSelectedWeek(1);
  }, [activeTime]);

  useEffect(() => {
    const payload = {
      type: TYPE_MAP[activeTime],
      graphfor: "builders", // Fixed to builders only
      selected_month: selectedMonth,
      selected_year: selectedYear,
      ...(activeTime === "Day" && { selected_week: selectedWeek }),
    };
    dispatch(getNewDashboardGraph(payload));
  }, [activeTime, selectedMonth, selectedYear, selectedWeek, dispatch]);

  const labels = useMemo(
    () => dashboardGraph?.data?.result?.map((i) => i.label) || [],
    [dashboardGraph]
  );

  const dataValues = useMemo(
    () => dashboardGraph?.data?.result?.map((i) => i.total) || [],
    [dashboardGraph]
  );

  const years = Array.from({ length: 6 }, (_, i) => curYear - 2 + i);

  const chartData = {
    labels,
    datasets: [
      {
        data: dataValues,
        fill: true,
        tension: 0.4,
        borderColor: "#2563eb",
        borderWidth: 2,
        borderDash: [6, 4],
        backgroundColor: (ctx) => {
          const canvas = ctx.chart.ctx;
          const { chartArea } = ctx.chart;
          if (!chartArea) return "rgba(37,99,235,0.08)";
          const gradient = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(37,99,235,0.18)");
          gradient.addColorStop(1, "rgba(37,99,235,0.01)");
          return gradient;
        },
        pointBackgroundColor: "#fff",
        pointBorderColor: "#2563eb",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e40af",
        titleColor: "#bfdbfe",
        bodyColor: "#fff",
        borderColor: "#93c5fd",
        borderWidth: 1,
        padding: 10,
        callbacks: { label: (ctx) => ` ${ctx.parsed.y}` },
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(0,0,0,0.05)", drawBorder: false },
        ticks: {
          color: "#64748b",
          font: { size: 12, family: "'DM Sans', sans-serif" },
          maxRotation: 45,
        },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.05)", drawBorder: false },
        ticks: {
          color: "#64748b",
          font: { size: 12, family: "'DM Sans', sans-serif" },
          stepSize: 1,
          callback: (v) => (Number.isInteger(v) ? v : null),
        },
        border: { display: false },
      },
    },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        .lg-wrap {
          font-family: 'DM Sans', sans-serif;
          background: #ffffff;
          border-radius: 16px;
          padding: 20px 24px 18px;
          width: 100%;
          box-sizing: border-box;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 6px rgba(0,0,0,0.06);
        }
        .lg-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
          flex-wrap: wrap;
          gap: 8px;
        }
        .lg-title {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }
        .lg-tabs { display: flex; gap: 4px; flex-wrap: wrap; }
        .lg-tab {
          background: none; border: none; cursor: pointer;
          font-family: 'DM Sans', sans-serif; font-size: 13px;
          font-weight: 400; color: #64748b;
          padding: 5px 12px; border-radius: 8px;
          position: relative; transition: all 0.2s;
        }
        .lg-tab:hover { background: #f1f5f9; color: #334155; }
        .lg-tab.active {
          color: #2563eb; font-weight: 600; background: #eff6ff;
        }
        .lg-tab.active::after {
          content: ''; position: absolute; bottom: 0;
          left: 12px; right: 12px; height: 2px;
          background: #2563eb; border-radius: 2px;
        }
        .lg-divider {
          height: 1px;
          background: #e2e8f0;
          margin: 10px 0;
        }
        .lg-filters {
          display: flex; gap: 8px;
          margin-bottom: 16px;
          align-items: center;
          flex-wrap: wrap;
        }
        .lg-select {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; color: #334155;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 5px 10px;
          outline: none;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .lg-select:focus { border-color: #2563eb; }
        .lg-chart-wrap { height: 220px; position: relative; }
        .lg-empty {
          min-height: 220px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          font-size: 14px;
        }
      `}</style>

      <div className="lg-wrap">
        <div className="lg-header">
          <p className="lg-title">Total Builders Graph</p>
          <div className="lg-tabs">
            {TIME_TABS.map((t) => (
              <button
                key={t}
                className={`lg-tab${activeTime === t ? " active" : ""}`}
                onClick={() => setActiveTime(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="lg-divider" />

        <div className="lg-filters">
          {activeTime !== "Year" && (
            <select
              className="lg-select"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(Number(e.target.value));
                setSelectedWeek(1);
              }}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          )}

          <select
            className="lg-select"
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(Number(e.target.value));
              setSelectedWeek(1);
            }}
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {activeTime === "Day" && (
            <select
              className="lg-select"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(Number(e.target.value))}
            >
              {Array.from({ length: totalWeeks }, (_, i) => (
                <option key={i + 1} value={i + 1}>Week {i + 1}</option>
              ))}
            </select>
          )}
        </div>

        {isLoading ? (
          <div className="lg-empty">Loading chart data...</div>
        ) : !dashboardGraph?.data?.result?.length ? (
          <div className="lg-empty">No data available for selected filters</div>
        ) : (
          <div className="lg-chart-wrap">
            <Line data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
    </>
  );
};

export default LineGraph;